import React from 'react';

/**
 * The icon set.
 *
 * The app was using 108 emoji as icons — the whole sidebar was `☰ 🖧 ⛁ ✓ ✉ ⚙
 * 👥 🛠`. Emoji are the wrong tool for this: they render as a different picture
 * on every platform, they cannot take the colour of what they sit in, they
 * carry their own baseline and refuse to align with text, and at small sizes
 * they read as coloured mush.
 *
 * These are stroked on a 24 grid and drawn in `currentColor`, so an icon is the
 * colour of its label without anyone having to say so — which is what makes a
 * disabled row, a hovered row and a selected row look right for free.
 *
 * Add one by putting a path in PATHS. Keep the stroke geometry (round caps,
 * round joins, 1.7 width) so the set stays one set.
 */

const PATHS = {
  // Navigation
  dashboard: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  workflow: <><rect x="3" y="4" width="6" height="5" rx="1.5" /><rect x="15" y="4" width="6" height="5" rx="1.5" /><rect x="9" y="15" width="6" height="5" rx="1.5" /><path d="M9 6.5h6M6 9v3.5a2 2 0 0 0 2 2h1M18 9v3.5a2 2 0 0 1-2 2h-1" /></>,
  leads: <><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" /><circle cx="10" cy="7.5" r="3.5" /><path d="M17.5 11.5 19 13l3-3.5" /></>,
  approvals: <><path d="M9 11.5 11.5 14 16 8.5" /><rect x="3" y="3" width="18" height="18" rx="3" /></>,
  conversations: <><path d="M20 13.5a2.5 2.5 0 0 1-2.5 2.5H8l-4 3.5V6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5Z" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 14.5a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-1 1.47V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.47-1 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 1-1.47V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.47 1Z" /></>,
  people: <><path d="M15 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 3 18.5V20" /><circle cx="9" cy="7.5" r="3.5" /><path d="M21 20v-1.5a3.5 3.5 0 0 0-2.6-3.38M15.5 4.13a3.5 3.5 0 0 1 0 6.74" /></>,
  integrations: <><path d="M14.5 4.5a2.5 2.5 0 0 1 3.54 0l1.46 1.46a2.5 2.5 0 0 1 0 3.54L17 12l-5-5Z" /><path d="m12 7-7 7v5h5l7-7" /><path d="m8 14 2 2" /></>,
  menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,

  // Objects and states
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  filter: <><path d="M3 5h18l-7 8v6l-4 2v-8Z" /></>,
  sort: <><path d="M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  close: <><path d="m6 6 12 12M18 6 6 18" /></>,
  check: <><path d="m5 12.5 4.5 4.5L19 7" /></>,
  chevronDown: <><path d="m6 9 6 6 6-6" /></>,
  chevronUp: <><path d="m6 15 6-6 6 6" /></>,
  chevronRight: <><path d="m9 6 6 6-6 6" /></>,
  chevronLeft: <><path d="m15 6-6 6 6 6" /></>,
  arrowRight: <><path d="M4 12h16M14 6l6 6-6 6" /></>,
  arrowLeft: <><path d="M20 12H4M10 6l-6 6 6 6" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3.5 7.5 7.2 5.1a2.2 2.2 0 0 0 2.6 0l7.2-5.1" /></>,
  phone: <><path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" /></>,
  link: <><path d="M10.5 13.5a4 4 0 0 0 5.66 0l2.84-2.84a4 4 0 1 0-5.66-5.66L11.7 6.6" /><path d="M13.5 10.5a4 4 0 0 0-5.66 0L5 13.34a4 4 0 1 0 5.66 5.66l1.63-1.63" /></>,
  building: <><path d="M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" /><path d="M14 10h4a2 2 0 0 1 2 2v9" /><path d="M2 21h20M7.5 8h3M7.5 12h3M7.5 16h3M17 14v3" /></>,
  document: <><path d="M14 3v4.5a1.5 1.5 0 0 0 1.5 1.5H20" /><path d="M19 9.8V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6.2Z" /></>,
  upload: <><path d="M12 16V4M12 4 7.5 8.5M12 4l4.5 4.5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></>,
  download: <><path d="M12 4v12M12 16l-4.5-4.5M12 16l4.5-4.5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></>,
  trash: <><path d="M4 6.5h16M9.5 6.5V4.8A1.3 1.3 0 0 1 10.8 3.5h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" /><path d="M6.5 6.5 7.4 19a2 2 0 0 0 2 1.9h5.2a2 2 0 0 0 2-1.9l.9-12.5" /><path d="M10.5 10.5v6M13.5 10.5v6" /></>,
  edit: <><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17Z" /><path d="m15 6 3 3" /></>,
  refresh: <><path d="M20 11A8 8 0 0 0 6.3 6.3L4 8.5" /><path d="M4 4.5v4h4" /><path d="M4 13a8 8 0 0 0 13.7 4.7L20 15.5" /><path d="M20 19.5v-4h-4" /></>,
  play: <><path d="M8 5.5 18.5 12 8 18.5Z" /></>,
  pause: <><path d="M9 5v14M15 5v14" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
  spark: <><path d="M12 3v4M12 17v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M3 12h4M17 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" /></>,
  alert: <><path d="M12 8.5V13" /><circle cx="12" cy="16.5" r="0.9" fill="currentColor" stroke="none" /><path d="M10.3 3.9 2.6 17.4A2 2 0 0 0 4.3 20.4h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>,
  info: <><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5" /><circle cx="12" cy="8" r="0.9" fill="currentColor" stroke="none" /></>,
  logout: <><path d="M14 5.5V4a1.5 1.5 0 0 0-1.5-1.5h-7A1.5 1.5 0 0 0 4 4v16a1.5 1.5 0 0 0 1.5 1.5h7A1.5 1.5 0 0 0 14 20v-1.5" /><path d="M9.5 12H21M21 12l-3.5-3.5M21 12l-3.5 3.5" /></>,
  brain: <><path d="M9.5 4a2.5 2.5 0 0 0-2.5 2.5A2.5 2.5 0 0 0 5 9v1a2.5 2.5 0 0 0 1 2v1a2.5 2.5 0 0 0 2.5 2.5H10V4Z" /><path d="M14.5 4A2.5 2.5 0 0 1 17 6.5 2.5 2.5 0 0 1 19 9v1a2.5 2.5 0 0 1-1 2v1a2.5 2.5 0 0 1-2.5 2.5H14V4Z" /><path d="M12 15.5V20" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" /></>,
  layers: <><path d="m12 3 9 4.5-9 4.5-9-4.5Z" /><path d="m3 12.5 9 4.5 9-4.5" /><path d="m3 17 9 4.5 9-4.5" /></>,
  more: <><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" /></>,
  external: <><path d="M14 4h6v6" /><path d="M20 4 11 13" /><path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10" /></>,
  power: <><path d="M12 3.5v8" /><path d="M17.7 6.8a8 8 0 1 1-11.4 0" /></>
};

/**
 * @param {string} name   a key of PATHS
 * @param {number} size   pixels, square
 */
function Icon({ name, size = 18, strokeWidth = 1.7, style, className, title }) {
  const path = PATHS[name];
  // A missing name renders nothing rather than an error glyph: an icon is
  // decoration next to a label, and a broken one should not become the message.
  if (!path) return null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flex: '0 0 auto', display: 'block', ...style }}
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
    >
      {title && <title>{title}</title>}
      {path}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS);
export default Icon;
